import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useParams } from "react-router-dom";
import axios from "axios";
import CommonButton from "../../components/CommonButton/CommonButton";
import CardTable from "./components/CardTable";
import AddCardDialog from "./components/AddCardDialog";
import ActivateCardDialog from "./components/ActivateCardDialog";
import DeactivateCardDialog from "./components/DeactivateCardDialog";
import { Card } from "./types";

const apiEnpoint: any = process.env.REACT_APP_API_BASE_URL;

const ManageMerchantCardsPage: React.FC = () => {
  const { businessId }: any = useParams();
  const [cards, setCards] = useState<Card[]>([]);
  const [status, setStatus] = useState<
    "idle" | "fetching" | "success" | "error"
  >("idle");
  const [error, setError] = useState<string>("");
  const [refreshing, setRefreshing] = useState(false);

  // Add Card Dialog State
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [cardType, setCardType] = useState("keycard");
  const [qrNumber, setQrNumber] = useState("");
  const [qrSuffix, setQrSuffix] = useState("");
  const [isAddLoading, setIsAddLoading] = useState(false);
  const [addError, setAddError] = useState<string>("");

  // Activate Dialog State
  const [isActivateDialogOpen, setIsActivateDialogOpen] = useState(false);
  const [activateQrcode, setActivateQrcode] = useState("");
  const [isActivateLoading, setIsActivateLoading] = useState(false);
  const [activateError, setActivateError] = useState<string>("");

  // Deactivate Dialog State
  const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false);
  const [deactivateQrcode, setDeactivateQrcode] = useState("");
  const [isDeactivateLoading, setIsDeactivateLoading] = useState(false);
  const [deactivateError, setDeactivateError] = useState<string>("");

  const fetchCards = async () => {
    try {
      setStatus("fetching");
      const response = await axios.get(
        `${apiEnpoint}/datacollectionbusiness/support/cards/business/${businessId}`,
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json"
          }
        }
      );
      setCards(
        response.data.map((card: any) => ({
          ...card,
          isActive: card.IsActive === 1
        }))
      );
      setStatus("success");
    } catch (err: any) {
      setStatus("error");
      setError(err.message || "Failed to fetch cards");
    }
  };

  useEffect(() => {
    if (businessId) {
      fetchCards();
    }
  }, [businessId]);

  const constructQrCode = () => {
    const cardTypes = [
      {
        value: "keycard",
        prefix: "06A",
        suffix: "A"
      }
    ];
    const selectedType =
      cardTypes.find((type) => type.value === cardType) || cardTypes[0];
    return `${selectedType.prefix}${qrNumber}${selectedType.suffix}${qrSuffix}`;
  };

  const handleAddCard = async () => {
    try {
      setIsAddLoading(true);
      setAddError("");
      const fullQrCode = constructQrCode();
      await axios.post(
        `${apiEnpoint}/datacollectionbusiness/support/cards`,
        {
          businessId,
          qrcode: fullQrCode
        },
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json"
          }
        }
      );
      setIsAddDialogOpen(false);
      setQrNumber("");
      setQrSuffix("");
      fetchCards();
    } catch (err: any) {
      setAddError(err.message || "Failed to add card");
    } finally {
      setIsAddLoading(false);
    }
  };

  const handleActivate = async (qrcode: string) => {
    setActivateQrcode(qrcode);
    setIsActivateDialogOpen(true);
  };

  const handleDeactivate = async (qrcode: string) => {
    setDeactivateQrcode(qrcode);
    setIsDeactivateDialogOpen(true);
  };

  const confirmActivate = async () => {
    try {
      setIsActivateLoading(true);
      setActivateError("");
      await axios.put(
        `${apiEnpoint}/datacollectionbusiness/cards/${activateQrcode}/activate`,
        {},
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json"
          }
        }
      );
      setIsActivateDialogOpen(false);
      fetchCards();
    } catch (err: any) {
      setActivateError(err.message || "Failed to activate card");
    } finally {
      setIsActivateLoading(false);
    }
  };

  const confirmDeactivate = async () => {
    try {
      setIsDeactivateLoading(true);
      setDeactivateError("");
      await axios.put(
        `${apiEnpoint}/datacollectionbusiness/cards/${deactivateQrcode}/deactivate`,
        {},
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json"
          }
        }
      );
      setIsDeactivateDialogOpen(false);
      fetchCards();
    } catch (err: any) {
      setDeactivateError(err.message || "Failed to deactivate card");
    } finally {
      setIsDeactivateLoading(false);
    }
  };

  const handleRefreshData = async () => {
    if (!refreshing) {
      setRefreshing(true);
      try {
        await fetchCards();
      } finally {
        setRefreshing(false);
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 3
        }}
      >
        <CommonButton
          variant="contained"
          onClick={() => setIsAddDialogOpen(true)}
        >
          Add New Card
        </CommonButton>
        <Box
          sx={{
            cursor: "pointer",
            ml: 2,
            display: "flex",
            alignItems: "center"
          }}
        >
          {refreshing ? (
            <CircularProgress size={25} color="inherit" />
          ) : (
            <RefreshIcon onClick={handleRefreshData} />
          )}
        </Box>
      </Box>

      <CardTable
        cards={cards}
        status={status}
        onActivate={handleActivate}
        onDeactivate={handleDeactivate}
      />

      <AddCardDialog
        open={isAddDialogOpen}
        onClose={() => {
          setIsAddDialogOpen(false);
          setQrNumber("");
          setQrSuffix("");
          setAddError("");
        }}
        onConfirm={handleAddCard}
        loading={isAddLoading}
        error={addError}
        cardType={cardType}
        onCardTypeChange={setCardType}
        qrNumber={qrNumber}
        onQrNumberChange={setQrNumber}
        qrSuffix={qrSuffix}
        onQrSuffixChange={setQrSuffix}
      />

      <ActivateCardDialog
        open={isActivateDialogOpen}
        onClose={() => {
          setIsActivateDialogOpen(false);
          setActivateError("");
        }}
        onConfirm={confirmActivate}
        loading={isActivateLoading}
        error={activateError}
      />

      <DeactivateCardDialog
        open={isDeactivateDialogOpen}
        onClose={() => {
          setIsDeactivateDialogOpen(false);
          setDeactivateError("");
        }}
        onConfirm={confirmDeactivate}
        loading={isDeactivateLoading}
        error={deactivateError}
      />
    </>
  );
};

export default ManageMerchantCardsPage;
